import React, {
  createContext,
  use,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react'
import {
  BuyerProposalOverviewDto,
  CreateProposalDto,
  FileUploadResponse,
} from '../shared/types'
import { ProposalsApi } from '../../api/proposals-api'

export interface UploadStatus {
  loaded: number
  total: number
  file: File
  success?: boolean
  error?: string
  fileId?: string | null
}

export interface FileUploadState {
  uploads: Record<string, UploadStatus> // filename -> status
  uploading: boolean
  hasErrors: boolean
}

interface ProposalsContextType {
  proposals: BuyerProposalOverviewDto[]
  isLoading: boolean
  error: Error | null
  addProposal: (proposal: CreateProposalDto) => Promise<string>
  getProposal: (id: string) => BuyerProposalOverviewDto | undefined
  updateProposal: (
    id: string,
    updates: Pick<BuyerProposalOverviewDto, 'status'>
  ) => Promise<boolean>
  uploadProposalFiles: (
    files: File[],
    retryMode?: boolean
  ) => Promise<FileUploadResponse[]>
  retryFailedUploads: () => Promise<FileUploadResponse[]>
  uploadState: FileUploadState
  resetUploadState: () => void
  refreshProposals: () => Promise<void>
}

const ProposalsContext = createContext<ProposalsContextType | undefined>(
  undefined
)

export const ProposalsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [proposalOverviews, setProposalOverviews] = useState<
    BuyerProposalOverviewDto[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [uploadState, setUploadState] = useState<FileUploadState>({
    uploads: {},
    uploading: false,
    hasErrors: false,
  })

  const refreshProposals = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const fetchedProposals = await ProposalsApi.getBuyerProposals()
      setProposalOverviews(fetchedProposals)
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch proposals')
      )
      console.error('Error fetching proposals:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshProposals()
  }, [refreshProposals])

  const resetUploadState = useCallback(() => {
    setUploadState({
      uploads: {},
      uploading: false,
      hasErrors: false,
    })
  }, [])

  const uploadProposalFiles = useCallback(
    async (files: File[], retryMode = false): Promise<FileUploadResponse[]> => {
      if (!retryMode) {
        setUploadState((prev) => ({
          ...prev,
          uploading: true,
          hasErrors: false,
          uploads: files.reduce(
            (acc, file) => ({
              ...acc,
              [file.name]: {
                file,
                loaded: 0,
                total: file.size,
              },
            }),
            {}
          ),
        }))
      } else {
        setUploadState((prev) => ({
          ...prev,
          uploading: true,
        }))
      }

      const uploadResults: FileUploadResponse[] = []

      for (const file of files) {
        const result = await ProposalsApi.uploadFile(file)
        uploadResults.push(result)

        setUploadState((prev) => ({
          ...prev,
          uploads: {
            ...prev.uploads,
            [file.name]: {
              ...prev.uploads[file.name],
              loaded: result.success ? file.size : 0,
              success: result.success,
              error: result.errorMessage,
              fileId: result.fileId,
            },
          },
          hasErrors: prev.hasErrors || !result.success,
        }))
      }

      setUploadState((prev) => ({
        ...prev,
        uploading: false,
      }))

      return uploadResults
    },
    []
  )

  const retryFailedUploads = useCallback(async (): Promise<
    FileUploadResponse[]
  > => {
    const failedFiles = Object.values(uploadState.uploads)
      .filter((status) => status.success === false)
      .map((status) => status.file)

    if (failedFiles.length === 0) {
      return []
    }

    return await uploadProposalFiles(failedFiles, true)
  }, [uploadState.uploads, uploadProposalFiles])

  const addProposal = useCallback(
    async (proposalData: CreateProposalDto) => {
      try {
        const proposalId = await ProposalsApi.createProposal(proposalData)
        await refreshProposals()

        return proposalId
      } catch (err) {
        console.error('Error creating proposal:', err)
        throw err
      }
    },
    [refreshProposals]
  )

  const getProposal = useCallback(
    (id: string) => {
      return proposalOverviews.find((p) => p.proposalId === id)
    },
    [proposalOverviews]
  )

  const updateProposal = useCallback(
    async (id: string, updates: Pick<BuyerProposalOverviewDto, 'status'>) => {
      try {
        if (updates.status) {
          await ProposalsApi.updateProposalStatus(id, updates.status)
        }

        await refreshProposals()
        return true
      } catch (err) {
        console.error(`Error updating proposal ${id}:`, err)
        return false
      }
    },
    [refreshProposals]
  )

  const contextValue = useMemo(
    () => ({
      proposals: proposalOverviews,
      isLoading,
      error,
      addProposal,
      getProposal,
      updateProposal,
      uploadProposalFiles,
      retryFailedUploads,
      uploadState,
      resetUploadState,
      refreshProposals,
    }),
    [
      proposalOverviews,
      isLoading,
      error,
      addProposal,
      getProposal,
      updateProposal,
      uploadProposalFiles,
      retryFailedUploads,
      uploadState,
      resetUploadState,
      refreshProposals,
    ]
  )

  return <ProposalsContext value={contextValue}>{children}</ProposalsContext>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useProposals = () => {
  const context = use(ProposalsContext)
  if (context === undefined) {
    throw new Error('useProposals must be used within a ProposalsProvider')
  }
  return context
}
